import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import isEqual from 'lodash/isEqual';

interface SelectListContextProps {
  ids: string[];
  selectedIds: string[];
  allSelected: boolean;
  lastTouchedId: Nullable<string>;
  disabledIds: string[];
  setIds: (id: string[]) => void;
  setSelectedIds: (id: string[]) => void;
  toggleSelectId: (id: string) => void;
  toggleSelectAll: () => void;
  setLastTouchedId: (id: Nullable<string>) => void;
  setDisabledIds: (id: string[]) => void;
}

interface SelectListProviderProps extends PropsWithChildren {
  initialIds?: string[];
}

export const SelectListContext = createContext<SelectListContextProps>(undefined!);

export const useSelectList = () => useContext(SelectListContext);

export const SelectListProvider: FC<SelectListProviderProps> = ({ initialIds, children }) => {
  const [ids, setIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [disabledIds, setDisabledIds] = useState<string[]>([]);
  const [lastTouchedId, setLastTouchedId] = useState<Nullable<string>>(null);

  const shiftHeld = useRef(false);

  const allSelected = selectedIds.length === ids.length - disabledIds.length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : filterDisabledIds(ids));
  };

  const disabledSet = useMemo(() => new Set(disabledIds), [disabledIds]);

  const filterDisabledIds = (idsOption: string[]) => {
    return idsOption.filter(id => !disabledSet.has(id));
  };

  const toggleSelectId = (id: string) => {
    if (shiftHeld.current) {
      const interval = getIdsInterval(id);
      const selectedSet = new Set(selectedIds);

      const eachChecked = interval.every(intervalId => selectedSet.has(intervalId));

      const updatedSet = new Set(selectedIds);
      if (eachChecked) {
        interval.forEach(intervalId => updatedSet.delete(intervalId));
      } else {
        interval.forEach(intervalId => updatedSet.add(intervalId));
      }

      setSelectedIds(filterDisabledIds(Array.from(updatedSet)));
    } else {
      if (selectedIds.includes(id)) {
        setSelectedIds(selectedIds.filter(idOption => idOption !== id));
      } else {
        setSelectedIds([...selectedIds, id]);
      }
    }

    setLastTouchedId(id);
  };

  const getIdsInterval = (endId: string) => {
    const startIdx = lastTouchedId ? ids.indexOf(lastTouchedId) : 0;
    const endIdx = ids.indexOf(endId);
    if (startIdx === -1 || endIdx === -1) return [];

    const [start, end] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
    return ids.slice(start, end + 1);
  };

  function logKey(e: MouseEvent) {
    shiftHeld.current = e.shiftKey;
  }

  const handleUpdateDisabledIds = (idsOption: string[]) => {
    if (!isEqual(idsOption, disabledIds)) {
      setDisabledIds(idsOption);
    }
  };

  useEffect(() => {
    if (!!initialIds && !isEqual(initialIds, ids)) {
      setIds(initialIds);
    }
  }, [initialIds]);

  useEffect(() => {
    if (selectedIds.some(selectedOption => !ids.includes(selectedOption))) {
      setSelectedIds(selectedIds.filter(selectedOption => ids.includes(selectedOption)));
    }
  }, [ids]);

  useEffect(() => {
    document.addEventListener('click', logKey, { capture: true });

    return () => {
      document.removeEventListener('click', logKey);
    };
  }, []);

  return (
    <SelectListContext.Provider
      value={{
        selectedIds,
        allSelected,
        lastTouchedId,
        ids,
        disabledIds,
        setIds,
        setSelectedIds,
        toggleSelectId,
        toggleSelectAll,
        setLastTouchedId,
        setDisabledIds: handleUpdateDisabledIds,
      }}
    >
      {children}
    </SelectListContext.Provider>
  );
};
